import { Controller } from "@hotwired/stimulus"
import { DataSet, Network } from "vis-network/standalone"
import { createBatchedUpdater, createProfileImageLoader, groupNodeIdsByImageUrl } from "../lib/x_profile_image_loader"

export default class extends Controller {
  static targets = ["container"]
  static values = { 
    tweetId: String,
    twUserId: String,
    defaultIcon: String
  }

  connect() {
    this.abortController = new AbortController()
    this.loadNetworkData(this.abortController.signal)
  }

  disconnect() {
    this.abortController?.abort()
    this.nodeImageUpdater?.cancel()
    if (this.network) {
      this.network.destroy()
    }
    this.network = null
    this.nodes = null
    // このコントローラが作った blob URL だけを revoke する(既定アイコンは対象外)
    this.profileImageLoader?.revokeAll()
  }

  async loadNetworkData(signal) {
    try {
      this.showLoading()
      
      const params = new URLSearchParams({
        'tweet[tweet_id]': this.tweetIdValue,
        'tweet[tw_user_id]': this.twUserIdValue
      })
      
      const response = await fetch(`/api/v1/connected-retweet-data?${params}`, { signal })
      const data = await response.json()
      
      this.renderNetwork(data, signal)
      this.loadProfileImages(data, signal)
      
    } catch (error) {
      // disconnect による中断はエラー表示しない
      if (signal.aborted || error?.name === 'AbortError') return

      console.error('Network data loading error:', error)
      this.showError('ネットワークデータの読み込みに失敗しました')
    } finally {
      this.hideLoading()
    }
  }

  renderNetwork(data, signal) {
    // disconnect の後に描画しない
    if (signal.aborted) return

    this.nodes = new DataSet(this.setNodes(data))
    const edges = this.setEdges(data)
    
    const networkData = {
      nodes: this.nodes,
      edges: edges
    }
    
    const options = this.setOptions()
    
    this.network = new Network(this.containerTarget, networkData, options)
    
    // クリックイベント設定
    this.network.on('click', (params) => {
      const nodeId = params.nodes[0]
      if (nodeId) {
        const nodeData = this.getNodeData(data, nodeId)
        if (nodeData) {
          window.open(`https://x.com/${nodeData.user_name}`, '_blank')
        }
      }
    })
  }

  setNodes(data) {
    const nodes = []
    
    data.retweets_info.forEach(retweet => {
      const node = {
        id: retweet.id,
        label: retweet.user_name,
        size: retweet.size,
        shape: 'circularImage',
        // まず既定アイコンで描画し、プロフィール画像は loadProfileImages で取れたものから差し替える
        image: this.defaultIconValue,
        borderWidth: 4,
        color: {
          border: "#167bff"
        }
      }
      
      // ルートノードは固定位置
      if (retweet.is_root === "true") {
        node.physics = false
        node.color.border = "#f31260"
      }
      
      nodes.push(node)
    })
    
    return nodes
  }

  // プロフィール画像を Referer なしで並列に取得し、取れたものをまとめてノードに反映する。
  // 1 件が遅い・失敗しても図の表示は遅れず、そのノードは既定アイコンのままになる
  loadProfileImages(data, signal) {
    if (signal.aborted) return

    const idsByUrl = groupNodeIdsByImageUrl(data.retweets_info)
    if (idsByUrl.size === 0) return

    this.profileImageLoader = createProfileImageLoader({
      fetch: (...args) => window.fetch(...args),
      createObjectURL: (blob) => URL.createObjectURL(blob),
      revokeObjectURL: (url) => URL.revokeObjectURL(url),
      setTimeout: (fn, ms) => window.setTimeout(fn, ms),
      clearTimeout: (id) => window.clearTimeout(id),
      signal
    })
    this.nodeImageUpdater = createBatchedUpdater({
      apply: (updates) => {
        if (signal.aborted || !this.nodes) return
        this.nodes.update(updates)
      },
      setTimeout: (fn, ms) => window.setTimeout(fn, ms),
      clearTimeout: (id) => window.clearTimeout(id)
    })

    idsByUrl.forEach((nodeIds, url) => {
      this.profileImageLoader.load(url).then((blobUrl) => {
        if (!blobUrl || signal.aborted) return
        this.nodeImageUpdater.push(nodeIds.map((id) => ({ id, image: blobUrl })))
      })
    })
  }

  setEdges(data) {
    const edges = []
    
    data.retweets_info.forEach(retweet => {
      retweet.tos.forEach(toId => {
        const edge = {
          from: retweet.id,
          to: toId,
          width: 2,
          length: 250,
          color: "#167bff",
          arrows: {
            to: {
              enabled: true,
              scaleFactor: 0.5
            }
          },
          scaling: {
            min: 10,
            max: 15
          }
        }
        
        // ルートからのエッジとその他で設定を変更
        if (retweet.is_root === "true") {
          edge.smooth = {
            enabled: false,
            type: 'discrete'
          }
          edge.color = "#f31260"
        } else {
          edge.smooth = {
            enabled: false,
            type: 'straightCross'
          }
        }
        
        edges.push(edge)
      })
    })
    
    return edges
  }

  setOptions() {
    return {
      nodes: {
        borderWidth: 4,
        brokenImage: this.defaultIconValue
      },
      physics: {
        solver: 'barnesHut',
        barnesHut: {
          gravitationalConstant: -8000,
          centralGravity: 1,
          springLength: 95,
          springConstant: 0.01,
          damping: 0.09,
          avoidOverlap: 0.9
        },
        stabilization: {
          fit: true
        },
        timestep: 0.5
      },
      interaction: {
        hover: true,
        tooltipDelay: 200
      },
      layout: {
        improvedLayout: true
      }
    }
  }

  getNodeData(data, nodeId) {
    return data.retweets_info.find(item => item.id == nodeId)
  }

  showLoading() {
    this.containerTarget.innerHTML = `
      <div class="flex items-center justify-center h-full">
        <span class="loading loading-spinner loading-lg"></span>
        <span class="ml-2">ネットワーク図を読み込み中...</span>
      </div>
    `
  }

  hideLoading() {
    // ローディング表示は renderNetwork で上書きされるため、特に処理不要
  }

  showError(message) {
    this.containerTarget.innerHTML = `
      <div class="flex items-center justify-center h-full">
        <div class="alert alert-error">
          <span class="iconify lucide--alert-circle size-5"></span>
          <span>${message}</span>
        </div>
      </div>
    `
  }
}
